import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Search, FileText, AlertCircle, DollarSign, CreditCard, Calendar } from 'lucide-react';
import Swal from 'sweetalert2';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [paymentRef, setPaymentRef] = useState('');
  const [comment, setComment] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await fetch('http://localhost:3009/salelist/orderhistory/3');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        // If each order can have multiple products, group them by sale_records_id:
        const grouped = {};
        data.forEach((order) => {
          const id = order.sale_records_id;
          if (!grouped[id]) {
            grouped[id] = {
              id: id,
              date: new Date(order.sale_date).toLocaleDateString(),
              status: order.dispatch_status,
              paymentMode: order.pmode || 'Cash',
              currency: order.currency || 'USD',
              trip_id: order.trip_id,
              products: [],
            };
          }
          grouped[id].products.push({
            quantity: order.qty_purchased,
            productName: order.product_name,
            unitPrice: order.selling_price,
            cost: order.unit_cost * order.qty_purchased,
          });
        });

        // Fetch trip details if needed
        const transformedOrders = await Promise.all(
          Object.values(grouped).map(async (order) => {
            let tripDetails = null;
            if (order.trip_id) {
              try {
                const tripResponse = await fetch(
                  `https://srv547457.hstgr.cloud:3011/trip/${order.trip_id}`
                );
                if (tripResponse.ok) {
                  const tripData = await tripResponse.json();
                  tripDetails = tripData.length > 0 ? tripData[0] : null;
                }
              } catch (e) {
                tripDetails = null;
              }
            }
            return {
              ...order,
              tripDetails,
            };
          })
        );

        setOrders(transformedOrders);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const toggleModal = (order = null) => {
    setSelectedOrder(order);
    setShowModal(!showModal);
    if (!showModal) {
      setPaymentRef('');
      setComment('');
    }
  };

  // FIXED: Defensive GET/PUT payment logic
  const updatePaymentRef = async () => {
    if (!selectedOrder || !paymentRef) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill in all required fields',
        icon: 'error',
      });
      return;
    }

    const payid = selectedOrder.id;

    Swal.fire({
      title: 'Updating...',
      text: 'Please wait while we update the payment reference.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const getResponse = await fetch(`http://localhost:3009/payments/${payid}`);
      if (!getResponse.ok) throw new Error('Failed to fetch payment data');
      let existingPayment = await getResponse.json();
      if (Array.isArray(existingPayment)) {
        if (existingPayment.length === 0) throw new Error('No payment found for this order');
        existingPayment = existingPayment[0];
      }
      if (!existingPayment) throw new Error('No payment found for this order');

      const updatedPayment = {
        ...existingPayment,
        ref: paymentRef,
        comment: comment
      };

      const updateResponse = await fetch(`http://localhost:3009/payments/${payid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedPayment),
      });

      if (!updateResponse.ok) throw new Error('Failed to update payment');

      await updateResponse.json();
      toggleModal();

      Swal.fire({
        title: 'Success!',
        text: 'Payment reference updated successfully!',
        icon: 'success',
      });

      // Refresh the orders list
      window.location.reload();
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: error.message,
        icon: 'error',
      });
    }
  };

  const filteredOrders = orders.filter(order =>
    order.products.some(prod =>
      prod.productName?.toLowerCase().includes(searchTerm.toLowerCase())
    ) ||
    order.id?.toString().includes(searchTerm)
  );

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'dis':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    if (!status) return 'Unknown';
    switch (status.toLowerCase()) {
      case 'dis':
        return 'Dispatched';
      default:
        return status;
    }
  };

  // Helper: compute totals for an order
  const getOrderTotal = (order) => {
    return order.products.reduce((sum, prod) => sum + (prod.unitPrice * prod.quantity), 0);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container mx-auto p-6 max-w-6xl"
    >
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800">Order History</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="animate-spin h-12 w-12 text-blue-500 mb-4" />
          <p className="text-gray-600">Loading orders...</p>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg">
          <AlertCircle className="text-red-500 mr-2" size={24} />
          <p className="text-red-500">{error}</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
          <FileText className="text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">No orders found</p>
        </div>
      ) : (
        <motion.div layout className="space-y-6">
          {filteredOrders.map((order) => (
            <motion.div
              key={order.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Order ID, Date, Status */}
                <div className="border-b md:border-b-0 md:border-r border-gray-200 pb-4 md:pb-0">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-lg font-bold text-gray-800">#{order.id}</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{order.date}</span>
                  </div>
                </div>

                {/* Order Details: PRODUCT COLUMN */}
                <div className="md:px-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Products</h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 text-gray-500 text-xs font-semibold pb-1 border-b border-gray-200">
                      <span>Qty</span>
                      <span>Product</span>
                      <span className="text-right">Price</span>
                    </div>
                    {order.products.map((prod, idx) => (
                      <div className="grid grid-cols-3 items-center py-1 text-sm" key={idx}>
                        <span className="font-medium">{prod.quantity}</span>
                        <span>{prod.productName}</span>
                        <span className="text-right">${prod.unitPrice.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-3 border-t border-gray-200 text-base">
                      <span className="text-gray-800 font-semibold">Total:</span>
                      <span className="font-bold text-blue-600">${getOrderTotal(order).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Action Section */}
                <div className="flex flex-col justify-center items-center md:items-end">
                  <button 
                    onClick={() => toggleModal(order)}
                    className="w-full md:w-auto px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Add Payment Ref
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Modal */}
      {showModal && selectedOrder && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md relative"
          >
            <button
              onClick={() => toggleModal()}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>
            <h3 className="text-xl font-semibold mb-6">Payment Reference Update</h3>
            
            {/* Payment Details Display */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Pay ID</p>
                  <p className="font-medium">{selectedOrder?.id}</p>
                </div>
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="font-medium">${getOrderTotal(selectedOrder).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Payment Mode</p>
                  <p className="font-medium">{selectedOrder?.paymentMode}</p>
                </div>
                <div>
                  <p className="text-gray-500">Currency</p>
                  <p className="font-medium">{selectedOrder?.currency}</p>
                </div>
              </div>
            </div>

            {/* Input Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Reference
                </label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter payment reference"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment
                </label>
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter comment"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex space-x-3">
              <button 
                onClick={() => toggleModal()}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button 
                onClick={updatePaymentRef}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
              >
                Update
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default OrderHistory;